import React from 'react';
import { observer } from 'mobx-react';
import { computed, observable } from 'mobx';
import ErrorBoundary from 'components/hoc/error_boundary.js';
import styled from '@emotion/styled';
import { dollar, cents , isPresent, isBlank } from '@seedlang/utils';
import Text from 'components/text';
import { AppUI, LoadingState } from '@seedlang/state';
import ExpandableTextDisplay from 'components/expandable_text_display';
import Spinner from 'components/spinner';
import { Theme } from '@seedlang/constants';

const WAITLIST_COLOR = '#C9791C';
const LEAVE_WAITLIST_BACKGROUND = '#FBE6CF';

const Wrapper = styled.div`
  height: auto;
  position: relative;
  cursor: auto;
  background: white;
  border: ${props => props.border};
  min-width: ${props => props.width};
  max-width: ${props => props.width};
  padding: 15px;
  display: flex;
  flex-basis: calc(50% - 10px);
  align-items: center;
  flex-direction: column;
  border-radius: 5px;
  box-shadow: 0 0 10px 1px #d0d0d0;
`;

const TopSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
`;

const AmountSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const Amount = styled.div`
  font-weight: bold;
  color: black;
  height: 46px;
`;

const AmountWrapper = styled.div`
  display: flex;
`;

const CurrencySymbol = styled.div`
  font-size: 36px;
  margin-top: -4px;
  font-family: 'Rubik' !important;
`;

const DollarAmount = styled.div`
  font-size: 60px;
  font-family: 'Rubik' !important;
`;

const CentsAmount = styled.div`
  font-size: 23px;
  margin-top: -8px;
  font-family: 'Rubik' !important;
`;

const Slash = styled.div`
  font-size: 20px;
`;

const Month = styled.div`
  font-size: 15px;
`;

const Image = styled.div`
  margin-bottom: 40px;
  img {
    border-radius: 5px;
    max-height: 150px;
    width: 100%;
    object-fit: contain;
  }
`;

const NotesWrapper = styled.div`
  height: 70px;
  display: flex;
  align-items: center;
`;

const Description = styled.div`
  font-size: 13px;
  color: #444;
  flex-direction: column;
  margin-top: 2.5px;
  line-height: 15px;
  margin-bottom: 2.5px;
  text-align: center;
  text-wrap: balance;
  display: grid;
  place-items: center;

  strong {
    color: ${Theme.darkBlue};
  }
`;

const FullDescription = styled.div`
  width: 100%;
  flex-direction: column;
  line-height: 20px;
  margin-top: 10px;
  margin-bottom: 20px;
  overflow: hidden;
  p {
    color: black;
  }
  ul {
    margin-left: 20px;
  }
  li {
    list-style-type: disc;
  }
  a {
    text-decoration: underline;
  }
  a:hover {
    text-decoration: none;
  }
`;

const ButtonWrapper = styled.div`
  width: 100%;
  text-align: center;
  margin-top: 20px;
  a {
    color: white;
    width: 100%;
  }
`;

const Button = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.background};
  font-size: 16px;
  padding: 25px;
  line-height: 0;
  width: 100%;
  color: ${props => props.color};
  border: ${props => props.border};
  border-radius: 50px;
  margin: 0;
  height: 50px;
  font-weight: bold;
  cursor: pointer;
  &:hover {
    transform: ${props => props.hoverTransform};
  }
  &:active {
    transform: ${props => props.clickTransform};
  }
`;

const Title = styled.div`
  margin-bottom: 5px;
  min-height: 50px;
  display: flex;
  align-items: center;
`;

const DiscountText = styled.div`
  position: relative;
  margin-top: 15px;
  margin-bottom: -5px;
  font-size: 14px;
  background: white;
  border: 1px solid darkgray;
  border-radius: 12px;
  padding: 10px 16px;
  text-align: center;
  color: #444;
  
  &::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 20px;
    width: 12px;
    height: 12px;
    background: white;
    border-left: 1px solid darkgray;
    border-top: 1px solid darkgray;
    transform: rotate(45deg);
    clip-path: polygon(0 0, 0 100%, 100% 0);
  }
`;

const DiscountLink = styled.span`
  text-decoration: underline;
  cursor: pointer;
`;

const TopBanner = styled.div`
  background: ${props => props.background};
  position: absolute;
  border-radius: 10px;
  font-size: 14px;
  font-weight: bold;
  color: #FFF;
  top: -10px;
  padding: 5px 20px;
`;

@observer
class MembershipOptionMemrev extends React.Component {
  @observable expanded = false;

  buttonLoadingState = new LoadingState();

  @computed get membershipMatchesCurrent() {
    if (this.props.paymentMigration) {
      return this.props.paymentMigration.membership?.id === this.props.membership.id;
    }
    return isPresent(this.props.activeSubscription) && this.props.activeSubscription.membership?.id === this.props.membership.id;
  }

  @computed get membershipTypeMatchesCurrent() {
    if (this.props.paymentMigration) { return false; }
    return isPresent(this.props.activeSubscription) && this.props.activeSubscription.membershipTypeId === this.props.membership?.membershipType?.id;
  }

  @computed get isNextPhaseMembership() {
    const nextPhaseId = this.props.activeSubscription?.nextPhaseMembership?.id;
    return isPresent(nextPhaseId) && nextPhaseId === this.props.membership?.id;
  }

  @computed get shouldShowWaitlistButton() {
    const membershipType = this.props.membership?.membershipType;
    return Number.isInteger(membershipType?.maxUserCount) &&
      membershipType.userCount >= membershipType.maxUserCount &&
      isPresent(membershipType.waitlistMailerliteGroupId) &&
      !this.membershipMatchesCurrent &&
      !this.membershipTypeMatchesCurrent &&
      !this.isNextPhaseMembership &&
      !this.props.hasMembershipChangePending;
  }

  @computed get buttonText() {
    if (AppUI.user.hasPermanentGroupMembership) {
      return '-';
    } else if (this.membershipMatchesCurrent) {
      return 'Current';
    } else if (this.isNextPhaseMembership) {
      const date = this.props.activeSubscription?.currentPeriodEnd;
      return date ? `Starts on ${date.formattedDateWithFullYear}` : 'Pending';
    } else if (this.membershipTypeMatchesCurrent) {
      if (this.props.membership?.numberMonths === 12) {
        return 'Switch to Yearly';
      } else if (this.props.membership?.numberMonths === 1) {
        return 'Switch to Monthly';
      } return 'Switch';
    } else if (this.shouldShowWaitlistButton) {
      return this.props.isOnWaitlist ? 'Leave Waitlist' : 'Join Waitlist';
    } else if (isPresent(this.props.activeSubscription?.membership) && this.props.activeSubscription.membership.position < this.props.membership.position) {
      return 'Upgrade';
    } else if (isPresent(this.props.activeSubscription?.membership) && this.props.activeSubscription.membership.position > this.props.membership.position) {
      return 'Downgrade';
    }

    return 'Join';
  }

  @computed get buttonColor() {
    if (this.shouldShowWaitlistButton) {
      return this.props.isOnWaitlist ? WAITLIST_COLOR : 'white';
    }
    return this.membershipMatchesCurrent ? AppUI.site.accentColor : 'white';
  }

  @computed get buttonBackgroundColor() {
    if (this.shouldShowWaitlistButton) {
      return this.props.isOnWaitlist ? LEAVE_WAITLIST_BACKGROUND : WAITLIST_COLOR;
    }
    if (this.membershipMatchesCurrent) return 'white';
    if (this.props.hasMembershipChangePending) return Theme.gray;
    return AppUI.site.accentColor;
  }

  @computed get buttonBorderColor() {
    if (this.shouldShowWaitlistButton) return WAITLIST_COLOR;
    if (this.membershipMatchesCurrent) return AppUI.site.accentColor;
    if (this.props.hasMembershipChangePending) return Theme.gray;
    return AppUI.site.accentColor;
  }

  handleButtonClick = async () => {
    if (this.buttonLoadingState.isLoading) return;
    if (this.props.hasMembershipChangePending) {
      this.props.onMembershipChangePendingClick();
      return;
    }
    if (this.shouldShowWaitlistButton) {
      this.buttonLoadingState.started();
      try {
        const membershipTypeId = this.props.membership.membershipType.id;
        if (this.props.isOnWaitlist) {
          await this.props.onLeaveWaitlist(membershipTypeId);
        } else {
          await this.props.onJoinWaitlist(membershipTypeId);
        }
        this.buttonLoadingState.succeeded();
      } catch (error) {
        this.buttonLoadingState.failed(error?.message || 'Unable to update the waitlist.');
      }
      return;
    }
    if (!this.membershipMatchesCurrent || this.props.mode === 'payment_migration') {
      this.props.onClick(this.props.membership, this.buttonLoadingState);
    }
  };

  @computed get border() {
    return this.props.membership.highlight ? `6px solid ${AppUI.site.accentColor}` : '6px solid #FFF';
  }

  @computed get membershipName() {
    if (isPresent(this.props.membership?.membershipType?.name)) {
      return this.props.membership?.membershipType?.name;
    } else {
      return this.props.membership.title || '';
    }
  }

  @computed get discountPercentage() {
    if (!this.pricingComparison) return null;

    const { monthlyCost, yearlyMonthlyCost } = this.pricingComparison;
    return Math.floor(((monthlyCost - yearlyMonthlyCost) / monthlyCost) * 100);
  }

  @computed get pricingComparison() {
    const membership = this.props.membership;
    if (!membership || ![1, 12].includes(membership.numberMonths)) return null;

    const compareNumberMonths = membership.numberMonths === 1 ? 12 : 1;
    const compareMembership = membership.matchingMembership(this.props.memberships, compareNumberMonths);

    if (!compareMembership) return null;

    const monthlyMembership = membership.numberMonths === 1 ? membership : compareMembership;
    const yearlyMembership = membership.numberMonths === 12 ? membership : compareMembership;
    const monthlyCost = monthlyMembership.costMonthly(this.props.currency);
    const yearlyMonthlyCost = yearlyMembership.costMonthly(this.props.currency);

    if (!Number.isFinite(monthlyCost) || !Number.isFinite(yearlyMonthlyCost)) return null;
    if (monthlyCost <= 0 || yearlyMonthlyCost >= monthlyCost) return null;

    return { monthlyCost, yearlyMonthlyCost };
  }

  @computed get discountAmount() {
    if (!this.pricingComparison) return null;

    const { monthlyCost, yearlyMonthlyCost } = this.pricingComparison;

    // For monthly view: show the monthly cost of yearly plan
    if (this.props.membership.numberMonths === 1) {
      // Format from cents to currency (e.g., 2400 -> 24.00)
      return (yearlyMonthlyCost / 100).toFixed(2);
    }

    // For yearly view: show how much they save per month
    if (this.props.membership.numberMonths === 12) {
      const monthlySavings = monthlyCost - yearlyMonthlyCost;
      // Format from cents to currency
      return (monthlySavings / 100).toFixed(2);
    }
    return null;
  }

  @computed get discountMode() {
    const membershipGroup = AppUI.user?.currentUserGroup?.membershipGroupId ? AppUI.membershipGroupStore.showData : AppUI.user?.site?.membershipGroup;
    return membershipGroup?.showDiscountAs || 'percentage';
  }

  @computed get description() {
    return [
      'All taxes included.',
      this.props.currency?.toUpperCase() === 'USD' ? this.props.membership.descriptionUsd : this.props.membership.descriptionEur,
      this.props.membership.numberMonths === 1 ? 'You can cancel at any time.' : 'Renews yearly, flexible cancellation after 1st year.',
    ].join(' ');
  }

  render() {
    return (
      <Wrapper
        className='MembershipOption__Wrapper'
        border={this.border}
        width={this.props.width}
      >
        {
          this.props.membership.highlight &&
            <TopBanner background={AppUI.site.accentColor}>{this.props.membership.highlightText}</TopBanner>
        }
        <TopSection>
          <Title>
            <Text
              fontSize='22px'
              bold
              color='black'
              textAlign='center'
            >
              <div
                dangerouslySetInnerHTML={{ __html: this.membershipName }}
              />
            </Text>
          </Title>
          {
            this.props.membership.imageUrl &&
              <Image>
                <img src={this.props.membership.imageUrl} alt='' />
              </Image>
          }
          <AmountSection>
            <Amount>
              <AmountWrapper>
                <CurrencySymbol>
                  {this.props.currencySymbol}
                </CurrencySymbol>
                <DollarAmount>
                  {this.props.membership.recurring ? dollar(this.props.membership.costMonthly(this.props.currency)) : dollar(this.props.membership.cost(this.props.currency))}
                </DollarAmount>
                <CentsAmount>
                  {this.props.membership.recurring ? cents(this.props.membership.costMonthly(this.props.currency)) : cents(this.props.membership.cost(this.props.currency))}
                </CentsAmount>
                {
                  this.props.membership.recurring &&
                    <Slash>
                      /
                    </Slash>
                }
                {
                  this.props.membership.recurring &&
                    <Month>
                      month
                    </Month>
                }
              </AmountWrapper>
            </Amount>

            {
              (this.discountPercentage || this.discountAmount) && this.props.membership?.numberMonths === 1 &&
              <DiscountText>
                {this.discountMode === 'amount'
                  ? <><div>Or only {this.props.currencySymbol}{this.discountAmount} per month</div><div>with <DiscountLink onClick={this.props.toggleYearly}>yearly&nbsp;billing</DiscountLink></div></>
                  : <>Switch to <DiscountLink onClick={this.props.toggleYearly}>yearly&nbsp;billing</DiscountLink> for {this.discountPercentage}% off.</>
                }
              </DiscountText>
            }
            {
              (this.discountPercentage || this.discountAmount) && this.props.membership?.numberMonths === 12 &&
              <DiscountText>
                {this.discountMode === 'amount'
                  ? <><div>You're saving {this.props.currencySymbol}{this.discountAmount} per month</div><div>on the yearly plan.</div></>
                  : <>You're saving {this.discountPercentage}% on the yearly plan</>
                }
              </DiscountText>
            }
          </AmountSection>
        </TopSection>
        {
          !AppUI.user.hasPermanentGroupMembership &&
            <ButtonWrapper>
              <Button
                color={this.buttonColor}
                background={this.buttonBackgroundColor}
                border={`4px solid ${this.buttonBorderColor}`}
                hoverTransform={!this.membershipMatchesCurrent && !this.isNextPhaseMembership ? 'scale(1.03)' : null}
                clickTransform={!this.membershipMatchesCurrent && !this.isNextPhaseMembership ? 'scale(0.97)' : null}
                onClick={this.handleButtonClick}
              >
                {
                  this.buttonLoadingState.isLoading
                    ? <Spinner background={this.buttonColor} margin='0' width='50px' height='10px' />
                    : this.buttonText
                }
              </Button>
            </ButtonWrapper>
        }
        <NotesWrapper>
          {
            isPresent(this.description) &&
              <Description>
                <strong>14-day money-back guarantee.</strong>
                {this.description}
              </Description>
          }
        </NotesWrapper>
        <FullDescription>
          <ExpandableTextDisplay
            paragraphLimit={this.props.expandable ? 3 : 100}
            markdownText={this.props.membership.descriptionMd}
            nonExpandedHeight={this.props.expandable ? '180px' : null}
            htmlText={isPresent(this.props.membership.description) && isBlank(this.props.membership.descriptionMd) && this.props.membership.description}
          />
        </FullDescription>
      </Wrapper>
    );
  }
}

export default ErrorBoundary(MembershipOptionMemrev);
